import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductManagement = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Fashion',
    gender: '',
    main_category: '',
    sub_category: '',
    colour: [],
    brand: '',
    fit: '',
    material: '',
    features: [],
    technology: '',
    best_for: '',
    sports: '',
    image_url: '',
    stock: ''
  });

  // Category Mapping - Same as App.js
  const categoryMapping = {
    Men: {
      Topwear: ['T-Shirts', 'Casual Shirts', 'Formal Shirts', 'Sweatshirts', 'Sweaters', 'Jackets', 'Blazers & Coats', 'Suits', 'Rain Jackets'],
      'Indian & Festive Wear': ['Kurtas & Kurta Sets', 'Sherwanis', 'Nehru Jackets', 'Dhotis'],
      Bottomwear: ['Jeans', 'Casual Trousers', 'Formal Trousers', 'Shorts', 'Track Pants & Joggers'],
      'Innerwear & Sleepwear': ['Briefs & Trunks', 'Boxers', 'Vests', 'Sleepwear & Loungewear', 'Thermals'],
      'Sports & Active Wear': ['Active T-Shirts', 'Track Pants & Shorts', 'Tracksuits', 'Jackets & Sweatshirts', 'Swimwear'],
      'Fashion Accessories': ['Ties, Cufflinks & Pocket Squares', 'Caps & Hats', 'Mufflers, Scarves & Gloves']
    },
    Women: {
      'Indian & Fusion Wear': ['Kurtas & Suits', 'Kurtis, Tunics & Tops', 'Sarees', 'Ethnic Wear', 'Leggings, Salwars & Churidars', 'Skirts & Palazzos', 'Dress Materials', 'Lehenga Cholis', 'Dupattas & Shawls', 'Jackets'],
      'Western Wear': ['Dresses', 'Tops', 'Tshirts', 'Jeans', 'Trousers & Capris', 'Shorts & Skirts', 'Co-ords', 'Playsuits', 'Jumpsuits', 'Shrugs', 'Sweaters & Sweatshirts', 'Jackets & Coats', 'Blazers & Waistcoats'],
      Maternity: [],
      'Sports & Active Wear': ['Sports T-Shirts & Tops', 'Track Pants & Shorts', 'Tracksuits', 'Sports Jackets', 'Swimwear'],
      'Lingerie & Sleepwear': ['Bra', 'Briefs', 'Shapewear', 'Sleepwear & Loungewear', 'Swimwear', 'Camisoles & Thermals']
    },
    Kids: {
      'Boys Clothing': ['T-Shirts', 'Shirts', 'Shorts', 'Jeans', 'Trousers', 'Clothing Sets', 'Ethnic Wear', 'Track Pants & Pyjamas', 'Jacket, Sweater & Sweatshirts', 'Party Wear', 'Innerwear & Thermals', 'Nightwear & Loungewear', 'Value Packs'],
      'Girls Clothing': ['Dresses', 'Tops', 'Tshirts', 'Clothing Sets', 'Lehenga choli', 'Kurta Sets', 'Party wear', 'Dungarees & Jumpsuits', 'Skirts & shorts', 'Tights & Leggings', 'Jeans, Trousers & Capris', 'Jacket, Sweater & Sweatshirts', 'Innerwear & Thermals', 'Nightwear & Loungewear', 'Value Packs'],
      Infants: ['Bodysuits', 'Rompers & Sleepsuits', 'Clothing Sets', 'Tshirts & Tops', 'Dresses', 'Bottom wear', 'Winter Wear', 'Innerwear & Sleepwear'],
      Brands: ['H&M', 'Max Kids', 'Pantaloons', 'United Colors Of Benetton Kids', 'YK', 'U.S. Polo Assn. Kids', 'Mothercare', 'HRX']
    }
  };

  const colourOptions = ['Black', 'Blue', 'Brown', 'Green', 'Grey', 'Multi-Colour', 'Orange', 'Pink', 'Purple', 'Red', 'White', 'Yellow'];
  const fitOptions = ['Oversized', 'Loose', 'Standard', 'Slim', 'Tight'];
  const materialOptions = ['Cotton', 'Polyester', 'Fleece', 'Tech Fleece', 'Recycled Materials', 'InfinaSoft', 'Leather', 'Denim', 'Wool'];
  const technologyOptions = ['Dri-FIT', 'Dri-FIT ADV', 'Storm-FIT', 'Therma-FIT'];
  const bestForOptions = ['Winter Wear', 'Warm Weather', 'Wet Weather Conditions', 'Casual', 'Formal', 'Sports'];
  const sportsOptions = ['Lifestyle', 'Running', 'Football', 'Training & Gym', 'Basketball', 'Yoga', 'Cricket'];
  const featuresOptions = ['Vented', 'Drawcord', 'Longline', 'Non-Padded Cups', 'Hooded', 'Pockets', 'Zipper'];

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    if (!token) {
      navigate('/login');
      return;
    }
    fetchProducts();
  }, [user, token, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?limit=100`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxSize = 800;
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          if (isEdit) {
            setEditingProduct({ ...editingProduct, image_url: compressedBase64 });
          } else {
            setFormData({ ...formData, image_url: compressedBase64 });
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const dataToSend = editingProduct || formData;
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct._id}`, dataToSend, config);
      } else {
        await axios.post(`${API}/products`, dataToSend, config);
      }
      
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${API}/products/${id}`, config);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct({ ...product });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'Fashion',
      gender: '',
      main_category: '',
      sub_category: '',
      colour: [],
      brand: '',
      fit: '',
      material: '',
      features: [],
      technology: '',
      best_for: '',
      sports: '',
      image_url: '',
      stock: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const currentData = editingProduct || formData;
  const setCurrentData = editingProduct ? setEditingProduct : setFormData;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-4 md:px-6 lg:px-8 md:py-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 md:mb-8 gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Products</h1>
            <p className="mt-1 md:mt-2 text-sm text-gray-600">{products.length} total</p>
          </div>
          <div className="flex gap-2 md:gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-3 md:px-4 py-2 border border-gray-300 rounded-md text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => {
                setEditingProduct(null);
                resetForm();
                setShowModal(true);
              }}
              className="px-3 md:px-4 py-2 bg-indigo-600 text-white rounded-md text-xs md:text-sm font-medium hover:bg-indigo-700"
            >
              Add Product
            </button>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <img src={product.image_url} alt={product.name} className="h-12 w-12 object-cover rounded" />
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                    <div className="text-sm text-gray-500">{product.brand}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{product.gender} - {product.main_category}</div>
                    <div className="text-sm text-gray-500">{product.sub_category}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{product.price}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      product.stock > 10 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {product.stock}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-3">
          {products.map((product) => (
            <div key={product._id} className="bg-white shadow rounded-lg p-4">
              <div className="flex gap-3">
                <img src={product.image_url} alt={product.name} className="h-20 w-20 object-cover rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">{product.name}</h3>
                  <p className="text-xs text-gray-500 truncate">{product.brand}</p>
                  <p className="text-xs text-gray-500 mt-1">{product.gender} - {product.main_category}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-semibold text-gray-900">₹{product.price}</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      product.stock > 10 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      Stock: {product.stock}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <button
                  onClick={() => handleEdit(product)}
                  className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded-md text-xs font-medium hover:bg-indigo-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(product._id)}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-start md:items-center justify-center z-50 overflow-y-auto p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl my-4 md:my-8 max-h-[calc(100vh-2rem)] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-4 md:px-8 py-4 z-10">
                <h2 className="text-lg md:text-xl font-bold">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-4 md:p-8 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input 
                    className="w-full border p-2 rounded text-sm" 
                    placeholder="Product Name" 
                    value={currentData.name} 
                    onChange={e => setCurrentData({ ...currentData, name: e.target.value })} 
                    required 
                  />
                  <input 
                    className="w-full border p-2 rounded text-sm" 
                    placeholder="Brand" 
                    value={currentData.brand || ''} 
                    onChange={e => setCurrentData({ ...currentData, brand: e.target.value })} 
                  />
                </div>
                
                <textarea 
                  className="w-full border p-2 rounded text-sm" 
                  placeholder="Description" 
                  rows="3" 
                  value={currentData.description} 
                  onChange={e => setCurrentData({ ...currentData, description: e.target.value })} 
                  required 
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <input 
                    className="w-full border p-2 rounded text-sm" 
                    placeholder="Price (₹)" 
                    type="number" 
                    value={currentData.price} 
                    onChange={e => setCurrentData({ ...currentData, price: e.target.value })} 
                    required 
                  />
                  <input 
                    className="w-full border p-2 rounded text-sm" 
                    placeholder="Stock" 
                    type="number" 
                    value={currentData.stock} 
                    onChange={e => setCurrentData({ ...currentData, stock: e.target.value })} 
                    required 
                  />
                </div>

                <div className="border-t pt-3 mt-3">
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Category Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Gender *</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.gender} 
                        onChange={e => setCurrentData({ ...currentData, gender: e.target.value, main_category: '', sub_category: '' })} 
                        required
                      >
                        <option value="">Select</option>
                        <option value="Men">Men</option>
                        <option value="Women">Women</option>
                        <option value="Kids">Kids</option>
                        <option value="Unisex">Unisex</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Main Category *</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.main_category} 
                        onChange={e => setCurrentData({ ...currentData, main_category: e.target.value, sub_category: '' })} 
                        required 
                        disabled={!currentData.gender || currentData.gender === 'Unisex'}
                      >
                        <option value="">Select</option>
                        {currentData.gender && currentData.gender !== 'Unisex' && categoryMapping[currentData.gender] && Object.keys(categoryMapping[currentData.gender]).map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Sub Category</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.sub_category} 
                        onChange={e => setCurrentData({ ...currentData, sub_category: e.target.value })} 
                        disabled={!currentData.main_category}
                      >
                        <option value="">Select</option>
                        {currentData.gender && currentData.main_category && categoryMapping[currentData.gender]?.[currentData.main_category]?.map(subcat => (
                          <option key={subcat} value={subcat}>{subcat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-3 mt-3">
                  <h3 className="font-semibold mb-2 text-sm md:text-base">Filter Attributes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Colour (Select multiple)</label>
                      <select 
                        multiple 
                        className="w-full border p-2 rounded h-24 text-sm" 
                        value={currentData.colour || []} 
                        onChange={e => setCurrentData({ ...currentData, colour: Array.from(e.target.selectedOptions, option => option.value) })}
                      >
                        {colourOptions.map(color => (
                          <option key={color} value={color}>{color}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Fit</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.fit || ''} 
                        onChange={e => setCurrentData({ ...currentData, fit: e.target.value })}
                      >
                        <option value="">Select</option>
                        {fitOptions.map(fit => (
                          <option key={fit} value={fit}>{fit}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Material</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.material || ''} 
                        onChange={e => setCurrentData({ ...currentData, material: e.target.value })}
                      >
                        <option value="">Select</option>
                        {materialOptions.map(material => (
                          <option key={material} value={material}>{material}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Technology</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.technology || ''} 
                        onChange={e => setCurrentData({ ...currentData, technology: e.target.value })}
                      >
                        <option value="">Select</option>
                        {technologyOptions.map(tech => (
                          <option key={tech} value={tech}>{tech}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Best For</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.best_for || ''} 
                        onChange={e => setCurrentData({ ...currentData, best_for: e.target.value })}
                      >
                        <option value="">Select</option>
                        {bestForOptions.map(best => (
                          <option key={best} value={best}>{best}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium mb-1">Sports</label>
                      <select 
                        className="w-full border p-2 rounded text-sm" 
                        value={currentData.sports || ''} 
                        onChange={e => setCurrentData({ ...currentData, sports: e.target.value })}
                      >
                        <option value="">Select</option>
                        {sportsOptions.map(sport => (
                          <option key={sport} value={sport}>{sport}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs md:text-sm font-medium mb-1">Features (Select multiple)</label>
                    <select 
                      multiple 
                      className="w-full border p-2 rounded h-20 text-sm" 
                      value={currentData.features || []} 
                      onChange={e => setCurrentData({ ...currentData, features: Array.from(e.target.selectedOptions, option => option.value) })}
                    >
                      {featuresOptions.map(feature => (
                        <option key={feature} value={feature}>{feature}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium mb-2">Product Image *</label>
                  <input 
                    type="file" 
                    accept="image/*"
                    className="w-full border p-2 rounded text-sm" 
                    onChange={(e) => handleImageUpload(e, !!editingProduct)}
                    required={!editingProduct && !currentData.image_url}
                  />
                  {currentData.image_url && (
                    <img src={currentData.image_url} alt="Preview" className="mt-2 w-32 h-32 object-cover rounded" />
                  )}
                </div>
                
                <div className="flex justify-end gap-2 pt-3 sticky bottom-0 bg-white border-t mt-4 -mx-4 md:-mx-8 px-4 md:px-8 py-3">
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowModal(false);
                      setEditingProduct(null);
                      resetForm();
                    }} 
                    className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
                  >
                    {editingProduct ? 'Update' : 'Add'} Product
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagement;
